import { ConfirmationDialog, Typography } from '@paintscout/ui'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import { getObjectLabels } from '@paintscout/util/builder'
import { getIndefiniteArticle } from '@paintscout/util/util'
import React, { useCallback } from 'react'

/**
 * The warning every manual status change goes through: picking a status by hand bypasses the
 * automation, so no thank-you email or notification goes out. Returns a function that shows the
 * warning and runs `onConfirm` only if the user accepts it.
 *
 * Shared by every place the status can be picked — the Estimate Settings dialog, the next editor's
 * Details card, and the document header's status pill — so the copy cannot drift between them. The
 * classic EditQuoteInfoDialog still carries its own copy of the same text.
 */
export function useConfirmStatusChange({ isInvoice }: { isInvoice?: boolean }) {
  const { options } = useClientOptions()
  const { openDialog, dismissDialog } = useDialogs()

  return useCallback(
    (onConfirm: () => void) => {
      const objectLabels = getObjectLabels({ options, invoice: isInvoice })
      openDialog(ConfirmationDialog, {
        message: (
          <Typography>
            Please note that manually changing the status of {getIndefiniteArticle(objectLabels.quote.value)}{' '}
            {objectLabels.quote.value.toLowerCase()} will not trigger any automatic thank-you emails or notifications.
          </Typography>
        ),
        onConfirm: () => {
          onConfirm()
          dismissDialog()
        },
        onCancel: () => dismissDialog()
      })
    },
    [options, isInvoice, openDialog, dismissDialog]
  )
}

export default useConfirmStatusChange
